import { defineStore } from 'pinia';
import { api } from '../boot/axios';
import { useFeatureFlags } from '@erebus/feature-flags';
import { useAuthStore } from './auth';
import { useLocationStore } from './location';

// The only keys the patient save endpoint uses. Sending only these keeps employee fields
// (password, role and so on) out of the request.
const patientSaveKeys = [
  'firstName',
  'surname',
  'sex',
  'birthDate',
  'approxDateFlag',
  'age',
  'email',
  'homeNumber',
  'cellNumber',
  'address',
  'clientID',
  'clientLocationID',
  'patientHealthCardNumber',
  'patientHealthCardType',
  'patientWeight',
  'patientHeight',
  'province',
  'allergies',
  'conditions',
  'medications',
  'immunizations',
  'habits',
];

const pickPatientSaveFields = (member) => {
  const body = {};
  for (const key of patientSaveKeys) {
    if (key in member) {
      body[key] = member[key];
    }
  }
  return body;
};

const getDefaultState = () => {
  return {
    memberIsLoading: false,
    loadingClients: [],
    members: [], // the members that the currently logged-in user has access to
    membersAreLoading: false,
    membersLoading: new Map(),
    doctors: [],
    clientMembers: {}, // [{clientID: 1, members: []}]
    clientPatients: {}, // same shape, built from patients (people_list_patients flag)
    employeeStatuses: [],
  };
};

export const useMemberStore = defineStore('member', {
  persist: {
    paths: ['memberIsLoading', 'members', 'doctors', 'clientMembers', 'employeeStatuses'],
  },

  state: () => getDefaultState(),

  getters: {
    employeePrimaryWorkLocationID: () => {
      const authStore = useAuthStore();
      return authStore.getUser.clientLocationID;
    },
    getEmployeeIsDoctor: (state) => (employeeID) => {
      return state.doctors.some((doc) => doc.value === employeeID);
    },
    employeeClientID: () => {
      const authStore = useAuthStore();
      return authStore.clientId;
    },
    getClientEmployees: (state) => (clientID) => {
      return state.members.filter((member) => member.clientID === clientID);
    },
    getMemberById: (state) => (employeeId) => {
      // Loose == would match a null id to patient-only rows (employeeID null).
      if (employeeId === null || employeeId === undefined || employeeId === '') {
        return {};
      }
      return state.members.find((member) => member.employeeID == employeeId) || {};
    },
    getMemberByPatientId: (state) => (patientId) => {
      // Try to find the member by patientID
      const member = state.members.find((member) => member.patientID === patientId) || {};
      // If found, return the member
      if (member) {
        return member;
      } else {
        this.fetchPatient(patientId);
        return {};
      }
    },
    getMemberByCallerId: (state) => (callerId) => {
      return state.members.find((member) => member.callerID == callerId) || {};
    },
    getEmployeeStatuses: (state) => {
      const stati = [];
      for (let i = 0; i < state.employeeStatuses.length; i++) {
        stati.push({
          label: state.employeeStatuses[i].employeeStatusName,
          value: state.employeeStatuses[i].employeeStatusID,
        });
      }
      return stati;
    },
    getEmployeeStatusByID: (state) => (statusID) => {
      return state.employeeStatuses.find((i) => i.employeeStatusID === statusID) || {};
    },
    getPatients: (state) => {
      return state.members.map((p) => {
        return {
          label: p.firstName + ' ' + p.surname,
          value: p.patientID,
        };
      });
    },
    getMembers: (state) => {
      const locationStore = useLocationStore();
      const authStore = useAuthStore();
      const locationIDs = locationStore.clientLocations
        .filter((location) => location.clientID === authStore.clientId)
        .map((location) => location.clientLocationID);
      return state.members.filter((member) => locationIDs.includes(member.clientLocationID));
    },
    getActiveMembersByClientID: (state) => (clientID) => {
      const locationStore = useLocationStore();
      const locationIDs = locationStore.clientLocations
        .filter((location) => location.clientID === clientID)
        .map((location) => location.clientLocationID);
      const activeStatuses = state.employeeStatuses
        .filter((status) => status.employeeStatusIsActive)
        .map((status) => status.employeeStatusID);
      return state.members.filter(
        (member) =>
          locationIDs.includes(member.clientLocationID) &&
          activeStatuses.includes(member.employeeStatusID),
      );
    },
    getMembersByClientID: (state) => (clientID) => {
      const locationStore = useLocationStore();
      const locationIDs = locationStore.clientLocations
        .filter((location) => location.clientID === clientID)
        .map((location) => location.clientLocationID);
      return state.members.filter((member) => locationIDs.includes(member.clientLocationID));
    },
    getMembersByLocation: (state) => (clientLocationId) => {
      return state.members.filter((member) => member.clientLocationID == clientLocationId) || {};
    },
    getClientPatients: (state) => (clientID) => {
      return state.clientPatients[clientID] || [];
    },
    getExternalMember: (state) => (clientID, employeeID) => {
      if (!state.clientMembers[clientID]) {
        return null;
      }
      return state.clientMembers[clientID].find((member) => member.employeeID === employeeID);
    },
    getFilteredMembers: (state) => {
      const clientMembers = state.getMembers;
      const authStore = useAuthStore();
      return clientMembers
        .filter((member) => {
          if (state.memberFilters.departmentID !== -1) {
            return member.departmentID === state.memberFilters.departmentID;
          }
          return member;
        })
        .filter((member) => {
          if (!authStore.isClientAdmin) {
            return member.clientLocationID === state.employeePrimaryWorkLocationID;
          }
          if (state.memberFilters.clientLocationID !== -1) {
            return member.clientLocationID === state.memberFilters.clientLocationID;
          }
          return member;
        })
        .filter((member) => {
          if (state.memberFilters.employeeStatusID !== -1) {
            return member.employeeStatusID === state.memberFilters.employeeStatusID;
          }
          return member;
        })
        .filter((member) => {
          if (state.memberFilters.employerID !== -1) {
            return member.employerID === state.memberFilters.employerID;
          }
          return member;
        })
        .filter((member) => {
          if (state.memberFilters.searchTerm !== null) {
            return (
              Object.values(member)
                .filter((value) => typeof value === 'string')
                .filter(
                  (value) =>
                    value
                      .toLocaleLowerCase()
                      .indexOf(state.memberFilters.searchTerm.toLocaleLowerCase()) >= 0,
                ).length > 0 ||
              ('' + member.patientHealthCardNumber).indexOf(state.memberFilters.searchTerm) !== -1
            );
          }
          return member;
        });
    },
  },

  actions: {
    async getDoctors() {
      await this.fetchDoctors();
      return this.doctors;
    },
    async fetchDocInfo() {
      const res = await api.get(`api/v1/employees/doctors`);
      return res.data;
    },
    async fetchClientDocInfo(clientID) {
      try {
        const response = await api.get('api/v1/employees/doctors', {
          params: {
            clientID: clientID,
          },
        });
        return response.data;
      } catch (error) {
        console.error(error);
      }
      return [];
    },
    async fetchReferralDoctor() {
      try {
        const res = await api.get('/api/v2/referrals/doctors');
        return res.data;
      } catch (error) {
        console.error(error);
        return [];
      }
    },
    fetchDoctors() {
      this.doctors.push({
        value: null,
        label: 'All Doctors',
      });
      try {
        api.get('api/v1/employees/doctors').then((response) => {
          for (let i = 0; i < response.data.length; i++) {
            this.doctors.push({
              value: response.data[i].employeeID,
              label: response.data[i].firstName + ' ' + response.data[i].surname,
            });
          }
        });
      } catch (e) {
        console.error(e.message);
      }
    },
    saveMemberLocally(newMember) {
      // Check to see if the new member is already present
      let present = false;
      if (this.members) {
        for (let i = 0; i < this.members.length; i++) {
          if (
            (this.members[i].employeeID && this.members[i].employeeID === newMember.employeeID) ||
            (this.members[i].patientID && this.members[i].patientID === newMember.patientID)
          ) {
            // found them, add in the new values
            this.members[i] = newMember;
            present = true;
          }
        }
      }
      // If not already present, add them to our store.
      if (!present) {
        this.members.push(newMember);
      }
      this.memberIsLoading = false;
    },
    async updateEmployeeStatus(employeeID, employeeStatusID) {
      await api.put(`/api/v2/employees/${employeeID}/status/${employeeStatusID}`);
      await this.fetchMember(employeeID);
    },
    saveMembersLocally(newMembers) {
      this.members = newMembers;
      this.members = newMembers.filter((member) => {
        return (
          (member.firstName != null && member.firstName !== 'null' && member.firstName !== '') || // OR
          (member.surname != null && member.surname !== 'null' && member.surname !== '')
        ); // and the surname is not ''
      });
      this.membersAreLoading = false;
    },
    saveStatusesLocally(newStatuses) {
      this.employeeStatuses = newStatuses;
    },
    saveFilterLocally(filter) {
      const field = Object.keys(filter)[0];
      this.memberFilters[field] = filter[field];
    },
    resetFilters() {
      Object.assign(this.memberFilters, getDefaultState().memberFilters);
    },
    async dataRefresh() {
      this.fetchDoctors();
      await this.fetchMembers();
      await this.fetchStatuses();
    },
    /**
     * This method will fetch the remote person if necessary. The local object is returned when the person already exists locally.
     * @param employeeId The person / employee ID of the person requested
     * @returns The person object
     */
    async fetchMember(employeeId) {
      if (!employeeId || isNaN(employeeId)) {
        return {};
      }

      // Check locally first.
      const localMember = this.members.find((m) => m.employeeID === employeeId);
      if (localMember !== undefined) {
        return localMember;
      }

      // A request for this employee may already be open. A case timeline mounts
      // one row per message and every row asks for its sender, so the same
      // employee gets asked for many times before the first answer arrives.
      // Share the open request instead of sending another.
      const inFlight = this.membersLoading.get(employeeId);
      if (inFlight !== undefined) {
        return await inFlight;
      }

      // Cleanup hangs off the request rather than off this caller's await, so
      // the entry is cleared once however many callers end up sharing it, and
      // a failed request does not leave one behind to block a retry.
      const request = this.forceFetchMember(employeeId).finally(() => {
        this.membersLoading.delete(employeeId);
      });
      this.membersLoading.set(employeeId, request);
      return await request;
    },
    async forceFetchMember(employeeID) {
      this.memberIsLoading = true;
      try {
        const res = await api.get(`/api/v1/employees/${employeeID}`, { skipAuthLogout: true });
        this.saveMemberLocally(res.data);
        return res.data;
      } catch (error) {
        console.error(error);
      } finally {
        this.memberIsLoading = false;
      }
    },

    /**
     * This method will fetch the remote person if necessary. The local object is returned when the person already exists locally.
     * @param patientId The patient ID of the person requested
     * @returns The person object
     */
    async fetchPatient(patientId) {
      if (!patientId) {
        return {};
      }
      this.memberIsLoading = true;
      return new Promise((resolve, reject) => {
        api
          .get('/api/v1/patients/' + patientId)
          .then((response) => {
            this.saveMemberLocally(response.data);
            resolve(response.data);
          })
          .catch((err) => {
            reject(err);
          })
          .finally(() => (this.memberIsLoading = false));
      });
    },
    fetchUser() {
      const authStore = useAuthStore();
      return new Promise((resolve, reject) => {
        this.fetchMember(authStore.employeeId)
          .then((newMember) => {
            authStore.user = newMember;
            resolve(newMember);
          })
          .catch((err) => {
            reject(err);
            console.error(err);
          });
      });
    },
    async fetchMembers() {
      // This will do a full refresh of the members from the server.
      this.membersAreLoading = true;
      const authStore = useAuthStore();
      const members = await this.fetchAllClientMembers(authStore.clientId);

      if (authStore.user?.employeeID) {
        const oldUser = { ...authStore.user }; // clone old user
        const newUserIndex = members.findIndex((m) => m.employeeID === oldUser.employeeID);
        const newUser = members[newUserIndex];
        const mergedUser = { ...oldUser, ...newUser };
        members.splice(newUserIndex, 1, mergedUser);
      }

      this.saveMembersLocally(members);
      this.membersAreLoading = false;
    },
    async fetchClientCallers(clientID) {
      const res = await api.get(`/api/v2/clients/${clientID}/callers`);
      return res.data;
    },
    async fetchAllClientMembers(clientID) {
      this.loadingClients.push(clientID);
      const res = await this.fetchClientMembers(clientID);
      let members = res.data;
      let result = members.length;
      while (result === 150 && result !== 0) {
        const offSet = members[members.length - 1].employeeID;
        const res = await this.fetchClientMembers(clientID, offSet);
        const moreMembers = res.data;
        members = members.concat(moreMembers);
        result = moreMembers.length;
      }
      this.clientMembers[clientID] = members;
      // Remove the client from the loading list
      this.loadingClients.splice(this.loadingClients.indexOf(clientID), 1);
      return members;
    },
    // Same paging as fetchAllClientMembers, but by patientID, so patients with
    // no employee row are included.
    async fetchAllClientPatients(clientID) {
      this.loadingClients.push(clientID);
      try {
        let patients = [];
        let page = await this.fetchClientPatients(clientID);
        patients = patients.concat(page);
        while (page.length === 150) {
          page = await this.fetchClientPatients(clientID, page[page.length - 1].patientID);
          patients = patients.concat(page);
        }
        this.clientPatients[clientID] = patients;
        return patients;
      } finally {
        this.loadingClients.splice(this.loadingClients.indexOf(clientID), 1);
      }
    },
    async fetchClientPatients(clientID, offSet) {
      let path = `/api/v3/clients/${clientID}/patients`;
      if (offSet !== undefined) {
        path = `${path}?offset=${offSet}`;
      }
      const res = await api.get(path);
      return res.data;
    },
    async fetchClientMembers(clientID, offSet) {
      let path = `/api/v2/clients/${clientID}/employees`;
      if (offSet !== undefined && offSet > 0) {
        path = `${path}?offset=${offSet}`;
      }
      try {
        return await api.get(path);
      } catch (error) {
        console.error(error);
      }
    },
    // Flag on: saves a patient with no employee row through the patient endpoints (PRX-2581).
    async postPatient(member) {
      this.memberIsLoading = true;
      try {
        const body = pickPatientSaveFields(member);
        const response = member.patientID
          ? await api.put(`/api/v3/patients/${member.patientID}`, body)
          : await api.post('/api/v3/patients', body);
        this.saveClientPatientRow(response.data);
        return await this.fetchSavedPatient(member, response.data);
      } finally {
        this.memberIsLoading = false;
      }
    },
    // The save returns a list row. Callers and PrCardPerson expect the full person, as before.
    // The save already worked, so a failed re-fetch must not look like a failed save:
    // the user would press save again and create a duplicate patient.
    async fetchSavedPatient(member, savedRow) {
      try {
        return await this.fetchPatient(savedRow.patientID);
      } catch {
        return { ...member, ...savedRow };
      }
    },
    // Replaces the row with the same patientID in clientPatients, or adds it.
    saveClientPatientRow(row) {
      const rows = this.clientPatients[row.clientID] || [];
      const index = rows.findIndex((existing) => existing.patientID === row.patientID);
      if (index === -1) {
        this.clientPatients[row.clientID] = [row, ...rows];
      } else {
        rows[index] = row;
        this.clientPatients[row.clientID] = rows;
      }
    },
    postMember(member) {
      const peopleListFromPatients = useFeatureFlags().isEnabled('people_list_patients').value;
      if (peopleListFromPatients && !member.employeeID) {
        return this.postPatient(member);
      }
      this.memberIsLoading = true;
      // We may be saving either an employee or a patient
      return new Promise((resolve, reject) => {
        if (!member.employeeID && !member.patientID) {
          api
            .post('api/v1/employees/', member)
            .then((response) => {
              this.saveMemberLocally(response.data);
              resolve(response.data);
            })
            .catch((err) => {
              console.error(err);
              reject(err);
            });
        } else if (member.employeeID) {
          api
            .put('/api/v1/employees/' + member.employeeID, member)
            .then((response) => {
              this.saveMemberLocally(response.data);
              const authStore = useAuthStore();
              if (authStore.employeeId === member.employeeID) {
                // commit(AUTH_SET_USER, response.data)
                const authStore = useAuthStore();
                authStore.user = response.data;
              }
              resolve(response.data);
            })
            .catch((err) => {
              reject(err);
            })
            .finally(() => (this.memberIsLoading = false));
        } else if (member.patientID) {
          api
            .put('/api/v1/patients/' + member.patientID, member)
            .then((response) => {
              this.saveMemberLocally(response.data);
              resolve(response.data);
            })
            .catch((err) => {
              reject(err);
            })
            .finally(() => (this.memberIsLoading = false));
        }
      });
    },
    fetchStatuses() {
      // This will do a full refresh of the statuses from the server.
      const authStore = useAuthStore();
      return new Promise((resolve, reject) => {
        api
          .get('/api/v1/clients/' + authStore.clientId + '/employeestatuses/')
          .then((response) => {
            this.saveStatusesLocally(response.data);
            resolve(response.data);
          })
          .catch((err) => {
            reject(err);
          });
      });
    },
    async fetchExistence(member) {
      return new Promise((resolve, reject) => {
        api
          .post('/api/v1/employees/checkEmployee', member)
          .then((response) => {
            resolve(response.data);
          })
          .catch((e) => {
            reject(e);
          });
      });
    },
  },
});
